// @ts-strict-ignore
import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {promisifyModal} from 'common/components/modal/utils';
import {getLocation} from 'common/util/url';
import {IronContextRegistry} from 'common/report/context-registry';

import {template} from './template';
import css from './index.inline.styl';
import {IntroStepName, IntroVersion, V2_STEPS} from './constants';
import {IntroState, IntroStep} from './types';
import './event-carousel';

const IntroStepTrackingLabel = {
  [IntroStepName.Mixpanel]: `Mixpanel`,
  [IntroStepName.Users]: `Users`,
  [IntroStepName.Events]: `Events`,
  [IntroStepName.Profiles]: `Profiles`,
  [IntroStepName.Complete]: `Complete`,
};

export class Intro extends Component<IntroState, TODO, TODO, TODO, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<TODO> {
    return {
      ...super.attrsSchema,
      'first-name': `string`,
      'joined-new-project': `boolean`,
      next: `string`,
      'is-full-page': `boolean`,
    };
  }

  get config(): ConfigOptions<IntroState, TODO, IronContextRegistry> {
    return {
      css,
      template,
      contexts: [`i18n` as const],
      useShadowDom: true,
      defaultState: {
        step: 0,
        visited: new Set(),
      },
      helpers: {
        IntroStepName,
        IntroVersion,
        handleStepClick: (step: number): void => {
          if (step !== this.state.step) {
            this.changeStep(step);
          }
        },
        nextStep: (): void => {
          this.changeStep(this.state.step + 1);
        },
        appendMpSource: (source: string): string => {
          if (!this.attr(`is-full-page`)) {
            return null;
          }
          const url = new URL(this.attr(`next`), getLocation().href);
          url.searchParams.append(`mp_source`, source);
          return url.href;
        },
        onCloseButtonClick: (): void => {
          this.dispatchEvent(new CustomEvent(`close`));
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get print() {
    return this.getContext(`i18n`).print;
  }

  connectedCallback() {
    super.connectedCallback();
    this.trackStep(this.state.step);
  }

  changeStep(nextStep: number): void {
    const {visited, step: currentStep} = this.state;
    this.update({
      step: Math.min(nextStep, this.numSteps - 1),
      visited: visited.add(currentStep),
    });

    this.trackStep(this.state.step);
  }

  getIntroSteps(): IntroStep[] {
    return V2_STEPS.map((step: IntroStep, index) => ({
      ...step,
      active: this.state.step === index,
      visited: this.state.visited.has(index),
    }));
  }

  getCtaCopy(): string {
    return this.attr(`joined-new-project`) ? this.t(`signup.intro.go-to-board`) : this.t(`signup.intro.go-to-mixpanel`);
  }

  private trackStep(step) {
    const stepName = this.getIntroSteps()[step].name;
    this.track(`Viewed Intro Step`, {'step index': step, 'step name': IntroStepTrackingLabel[stepName]});
  }

  private track(eventName, props = {}) {
    mixpanel.track(`[Onboarding] ${eventName}`, {
      ...props,
      'is full page': this.attr(`is-full-page`),
      version: IntroVersion.V2,
    });
  }

  private get numSteps() {
    return V2_STEPS.length;
  }
}

export async function promptIntroModal(attrs): Promise<boolean> {
  await promisifyModal({
    tagName: `mp-intro`,
    attrs,
    closeEvents: [`close`],
  });
  return false;
}

export const IntroEl = defineAndMakeJsxFactory(`mp-intro`, Intro);
export const IntroReact = panel2react(`mp-intro`, Intro);
